/**
 * TARİH ARALIĞI SEÇİCİ — "Dönem" (hazır seçenekler) + Başlangıç + Bitiş.
 *
 * Sitede iki tarih arası seçilen HER yer bunu kullanır (kullanıcı,
 * 12.10.2026). Hazır dönem seçilince iki tarih ona göre dolar; tarihler elle
 * değişince dönem kendiliğinden "Özel aralık" olur (ya da elle girilen aralık
 * hazır bir döneme denk geliyorsa o dönem görünür). Dönemlerin tanımı
 * `lib/tarihDonemleri.ts`te, tek kopya.
 *
 * Üç alanı bir PARÇA olarak döner: çağıranın ızgarasına / satırına oturur.
 */

import { useState } from "react";
import type { IsoDate } from "@parafon/contracts";
import {
  DONEMLER,
  bugunYerelIso,
  donemAraligi,
  type DonemKodu,
  type TarihAraligi,
} from "../lib/tarihDonemleri";
import { DateInput, Field, Select, cx } from "./primitives";
import { M } from "../dil";

const OZEL = "ozel";
const TUMU = "tumu";

export interface TarihAraligiSeciciProps {
  readonly from: IsoDate;
  readonly to: IsoDate;
  /** `donem`: hazır listeden seçildiyse onun kodu, tarih elle değiştiyse null. */
  readonly onChange: (aralik: TarihAraligi, donem: DonemKodu | null) => void;
  /** Başlangıçta seçili hazır dönem (aynı aralığa iki dönem denk gelirse hangisi). */
  readonly donem?: DonemKodu | null;
  /** Alan kimliklerinin öneki (`<onek>-donem`, `<onek>-bas`, `<onek>-son`). */
  readonly idOnek: string;
  /** Boş aralık (filtre yok) geçerliyse: "Tüm tarihler" seçeneği. */
  readonly bosOlabilir?: boolean;
  /** Bitişin en geç değeri (ör. bugün). */
  readonly max?: IsoDate;
  /**
   * En eski seçilebilir gün (gelir modeli: fiyat geçmişi derinliği). Bundan
   * önce başlayan hazır dönemler listede çıkmaz; başlangıç alanı bunun
   * öncesine izin vermez.
   */
  readonly min?: IsoDate;
  /** Testler için: "bugün" (varsayılan kullanıcının yerel günü). */
  readonly bugun?: IsoDate;
  readonly alanSinifi?: string;
  /** "Dönem" alanına ayrıca (ör. telefonda tam satır). */
  readonly donemSinifi?: string;
}

export function TarihAraligiSecici({
  from,
  to,
  onChange,
  idOnek,
  bosOlabilir = false,
  max,
  min,
  bugun = bugunYerelIso(),
  alanSinifi,
  donemSinifi,
  donem = null,
}: TarihAraligiSeciciProps) {
  // Aynı aralığa birden çok dönem denk gelebilir (pazartesi "bu hafta" =
  // "bugün"): kullanıcının seçtiği, aralık hâlâ onunsa görünür.
  const [secilen, setSecilen] = useState<DonemKodu | null>(donem);
  const denk = (kod: DonemKodu) => {
    const a = donemAraligi(kod, bugun);
    return a.from === from && a.to === to;
  };
  const gorunen =
    bosOlabilir && from === "" && to === ""
      ? TUMU
      : secilen !== null && denk(secilen)
        ? secilen
        : (DONEMLER.find((d) => denk(d.kod))?.kod ?? OZEL);

  const secenekler = [
    ...(bosOlabilir ? [{ value: TUMU, label: M.grafik.aralik.tumTarihler }] : []),
    ...DONEMLER.filter((d) => min === undefined || d.aralik(bugun).from >= min).map((d) => ({ value: d.kod, label: d.ad })),
    ...(gorunen === OZEL ? [{ value: OZEL, label: M.grafik.aralik.ozel }] : []),
  ];

  return (
    <>
      <Field label={M.grafik.aralik.donem} htmlFor={`${idOnek}-donem`} className={cx(alanSinifi, donemSinifi)}>
        <Select
          id={`${idOnek}-donem`}
          value={gorunen}
          options={secenekler}
          onChange={(e) => {
            const v = e.currentTarget.value;
            if (v === TUMU) {
              setSecilen(null);
              onChange({ from: "", to: "" }, null);
            } else if (v !== OZEL) {
              const kod = v as DonemKodu;
              setSecilen(kod);
              onChange(donemAraligi(kod, bugun), kod);
            }
          }}
        />
      </Field>
      <Field label={M.grafik.aralik.baslangic} htmlFor={`${idOnek}-bas`} className={cx(alanSinifi)}>
        <DateInput
          id={`${idOnek}-bas`}
          value={from}
          {...(min === undefined ? {} : { min })}
          max={to === "" ? max : to}
          onChange={(e) => onChange({ from: e.currentTarget.value, to }, null)}
        />
      </Field>
      <Field label={M.grafik.aralik.bitis} htmlFor={`${idOnek}-son`} className={cx(alanSinifi)}>
        <DateInput id={`${idOnek}-son`} value={to} max={max} onChange={(e) => onChange({ from, to: e.currentTarget.value }, null)} />
      </Field>
    </>
  );
}
